// Llegada desde un curso con QR (13-oct-2026, cuidados paliativos; desde el
// 15-oct también accesos vasculares, ver CURSOS).
//
// El video de cada curso cierra con un QR a
// https://mipae.cl/?utm_source=feneech&utm_medium=curso&utm_campaign=<campaña>
// y dice "Escanea el código QR y trabaja este caso con Benito en la web de
// MiPAE". Quien llega por ahí ve el MISMO caso ficticio y la MISMA pregunta del
// video, y un botón para preguntársela a Benito. El archivo conserva su nombre
// porque nació con el primer curso.
//
// Reglas de esta pieza:
// - Se reconoce por la campaña de la dirección o por la atribución que la
//   portada ya guarda antes del login (lib/landingTracking, `mipae:attrib`),
//   así sobrevive a la vuelta de Google o Apple sin guardar nada nuevo.
// - Benito no se abre solo: la pregunta queda escrita en su panel SOLO cuando
//   la persona toca el botón, y la envía ella.
// - Sin logos ni nombres de instituciones, sin precios ni beneficios.
// - Cada pieza se retira sola pasado su `hasta` y se puede cerrar con la X.

import { atribucionGuardada, mergeAtribucion, parseAtribucion } from './landingTracking';

export const CAMPANA_CURSO = 'paliativos-2026-10';

// Último día en que se muestra, en hora de Chile (inclusive). El curso tiene
// clases el 13 y el 27 de octubre; queda un mes de holgura para quien escanee
// tarde.
export const CURSO_HASTA = '2026-11-30';

// El caso y la pregunta son los del video, palabra por palabra.
export const CASO_CURSO = 'Persona con cáncer avanzado, en cuidados paliativos. Refiere dolor y expresa temor por lo que está viviendo.';
export const PREGUNTA_CURSO = 'Estoy estudiando cuidados paliativos. En un caso ficticio, una persona con cáncer avanzado refiere dolor y expresa temor por lo que está viviendo. ¿Qué otras dimensiones debería explorar en la valoración y por qué?';

// Los cursos con QR (catálogo cerrado). Desde el 15-oct-2026 son dos: al de
// paliativos se sumó el de accesos vasculares (clases el 15 y el 16 de octubre),
// con su propio video y su propia campaña. Cada uno trae su caso y su pregunta,
// los mismos del video, y se retira solo pasado `hasta`. El primero es el de
// paliativos y conserva las constantes de arriba, que el resto de la app y sus
// pruebas ya usaban.
export const CURSOS = [
  {
    campana: CAMPANA_CURSO,
    nombre: 'cuidados paliativos',
    hasta: CURSO_HASTA,
    caso: CASO_CURSO,
    pregunta: PREGUNTA_CURSO,
  },
  {
    campana: 'accesos-vasculares-2026-10',
    nombre: 'accesos vasculares',
    hasta: '2026-11-30',
    caso: 'Persona hospitalizada en la UCI con un catéter venoso central en la yugular derecha y una vía venosa periférica en el antebrazo, instalada hace tres días, que le duele. Cuando pase a sala le van a instalar un PICC.',
    pregunta: 'Estoy estudiando accesos vasculares. En un caso ficticio, una persona hospitalizada en la UCI tiene un catéter venoso central en la yugular derecha y una vía venosa periférica en el antebrazo, instalada hace tres días, que le duele. Cuando pase a sala le van a instalar un PICC. ¿Qué debería valorar en el sitio de cada acceso y por qué?',
  },
];

const cerradoKey = (campana) => `mipae:curso:${campana}:cerrado`;

// "AAAA-MM-DD" de hoy en Chile. Si el navegador no sabe de zonas horarias,
// cae a la fecha local del aparato (para el corte de una fecha da igual).
export function fechaChile(ahora = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(ahora);
  } catch {
    const p = (n) => String(n).padStart(2, '0');
    return `${ahora.getFullYear()}-${p(ahora.getMonth() + 1)}-${p(ahora.getDate())}`;
  }
}

// Puro: el curso de esta atribución, si su pieza sigue vigente (o null).
export function cursoDeAtribucion(atrib, ahora = new Date()) {
  if (!atrib) return null;
  const curso = CURSOS.find((c) => c.campana === atrib.utm_campaign);
  if (!curso) return null;
  return fechaChile(ahora) <= curso.hasta ? curso : null;
}

// Puro: ¿esta atribución viene de un curso y la pieza sigue vigente?
export function vieneDelCurso(atrib, ahora = new Date()) {
  return cursoDeAtribucion(atrib, ahora) !== null;
}

// La atribución de ESTA visita gana sobre la guardada (misma regla que
// mergeAtribucion). Se lee la dirección además de lo guardado porque App
// guarda la atribución en un efecto, después del primer dibujo.
// Nunca rompe el inicio: ante cualquier falla, no hay curso.
export function atribucionActual(search) {
  try {
    const s = search != null ? search : (typeof window !== 'undefined' ? window.location.search : '');
    return mergeAtribucion(atribucionGuardada(), parseAtribucion(s, null));
  } catch {
    return null;
  }
}

// La X cierra la pieza de ESE curso en este aparato: quien cerró el de
// paliativos y después escanea el de accesos vasculares igual ve el nuevo.
export function cursoCerrado(campana = CAMPANA_CURSO) {
  try { return localStorage.getItem(cerradoKey(campana)) === '1'; } catch { return false; }
}

export function cerrarCurso(campana = CAMPANA_CURSO) {
  try { localStorage.setItem(cerradoKey(campana), '1'); } catch { /* sin storage: se cierra solo en esta vista */ }
}

// El curso cuya pieza se muestra ahora, o null (con sesión, además, respeta la X).
export function cursoActivo({ conSesion = false, ahora = new Date(), search } = {}) {
  const curso = cursoDeAtribucion(atribucionActual(search), ahora);
  if (!curso) return null;
  return conSesion && cursoCerrado(curso.campana) ? null : curso;
}

// ¿Se muestra la pieza del curso ahora?
export function mostrarCurso(opciones = {}) {
  return cursoActivo(opciones) !== null;
}
